import PropTypes from 'prop-types';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import AddRounded from '@mui/icons-material/AddRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import LaunchRounded from '@mui/icons-material/LaunchRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import WarningRounded from '@mui/icons-material/WarningRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  alpha,
  useColorScheme,
} from '@mui/material/styles';

import {
  addTenantUserRequest,
  requestTenants,
} from '@pingum/app-state-management/tenant/actions';
import {
  TENANT_USER_REQUEST_ADD_SUCCESS,
  TENANTS_SUCCESS,
} from '@pingum/app-state-management/tenant/types';

import { primary } from '../../../utils/brandColors';
import {
  getQuestriPrimaryButtonSx,
  questriOrange,
} from '../../../utils/questriButtonStyles';
import useDebounce from '../../../utils/useDebounce';
import AppStoreButton from '../../AppStoreButton';
import TenantAvatar from '../../Tenant/TenantAvatar';
import CreateOrganizationCard from '../CreateOrganization';

const LimitedPaidClientAccessCardComponent = ({
  barns,
  authUser,
}) => {
  const dispatch = useDispatch();
  const {
    mode,
    systemMode,
  } = useColorScheme();
  const isDark = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
  const location = useLocation();
  const navigate = useNavigate();
  const createOrganization = new URLSearchParams(location.search).get('signup') === 'true';

  const openCreateOrganization = () => {
    const params = new URLSearchParams(location.search);
    params.set('signup', 'true');
    navigate({
      pathname: location.pathname,
      search: params.toString(),
      hash: location.hash,
    }, {
      state: {
        ...location.state,
        fromBarnChooser: true,
      },
    });
  };

  const closeCreateOrganization = () => {
    if (location.state?.fromBarnChooser) {
      navigate(-1);
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete('signup');
    navigate({
      pathname: location.pathname,
      search: params.toString(),
      hash: location.hash,
    }, { replace: true });
  };

  // redux state
  const { isAddingTenant: isLoading } = useSelector((state) => state.tenant);
  const isSearchingTenants = useSelector((state) => state.tenant.isFetching);
  const tenants = useSelector((state) => state.tenant.tenants);

  const profile = useSelector((state) => state.user.user);
  const barnUser = useSelector((state) => state.user.barnUser);

  const hasProfile = (profile && Object.keys(profile).length > 0)
    || (barnUser && Object.keys(barnUser).length > 0);

  // local state
  const [search, setSearch] = React.useState('');
  const [hasSearched, setHasSearched] = React.useState(false);
  const [requestedBarnName, setRequestedBarnName] = React.useState('');

  const debouncedSearch = useDebounce(search, 500);

  React.useEffect(() => {
    let isCurrentRequest = true;
    if (debouncedSearch.trim().length >= 3) {
      setHasSearched(false);
      // Forward the shared thunk's actions so only this search can mark its results ready.
      requestTenants(debouncedSearch)((action) => {
        dispatch(action);
        if (isCurrentRequest && action.type === TENANTS_SUCCESS) setHasSearched(true);
      });
    }
    return () => { isCurrentRequest = false; };
  }, [debouncedSearch, dispatch]);

  const isFetching = isLoading;

  const handleRequestAccess = (barn) => {
    setRequestedBarnName('');
    addTenantUserRequest(barn.name, {
      auth0UserId: authUser.sub,
      email: authUser.email,
      firstName: (authUser && authUser['https://pingum.io/firstName']) || '',
      lastName: (authUser && authUser['https://pingum.io/lastName']) || '',
      passive: false,
    })((action) => {
      dispatch(action);
      if (action.type === TENANT_USER_REQUEST_ADD_SUCCESS) setRequestedBarnName(barn.displayName);
    });
  };

  const isCurrentSearch = search.trim().length >= 3 && search === debouncedSearch;
  // Only show matching Questri Barns the user has not joined.
  const foundTenants = (hasSearched && isCurrentSearch && tenants
    ? tenants
    : []).filter((t) => t.application === 'questri' && barns.map((existingTenant) => existingTenant.tenant).indexOf(t.name) < 0);

  const pageBackground = isDark
    ? '#171A18'
    : '#F4F1EA';
  const surfaceBackground = isDark
    ? '#202520'
    : '#FFFFFF';
  const accentColor = isDark
    ? '#E9AA7B'
    : '#8A4B26';
  const introduction = barns.length > 0
    ? 'Choose a Barn to pick up where you left off, or start something new.'
    : 'Create your own Questri Barn, or join a team already using Questri.';
  const panelSx = {
    p: {
      xs: 2.5,
      sm: 4,
    },
    backgroundColor: surfaceBackground,
    borderRadius: 2,
    boxShadow: 'none',
    minWidth: 0,
  };
  const barnTextSx = {
    minWidth: 0,
    overflowWrap: 'anywhere',
    '& .MuiListItemText-primary': {
      fontSize: 16,
      fontWeight: 600,
    },
    '& .MuiListItemText-secondary': {
      fontSize: 13,
      mt: 0.5,
    },
  };

  if (createOrganization) {
    return <CreateOrganizationCard onCancel={closeCreateOrganization} />;
  }

  return (
    <Box
      sx={{
        backgroundColor: pageBackground,
        minHeight: 'calc(100vh - 48px)',
        mb: -6,
        px: 2,
        py: {
          xs: 4,
          sm: 6,
        },
        '& p': { letterSpacing: 0 },
      }}
    >
      <Box
        sx={{
          maxWidth: 1040,
          mx: 'auto',
        }}
      >
        <Box component="header" sx={{ mb: 4 }}>
          <Typography
            variant="overline"
            sx={{
              color: accentColor,
              fontWeight: 600,
              letterSpacing: '0.12em',
            }}
          >
            Your daily barn life, connected
          </Typography>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: 30,
                sm: 40,
              },
              fontWeight: 600,
              letterSpacing: '-0.04em',
              lineHeight: 1.2,
              mt: 1,
              mb: 1.5,
            }}
          >
            Welcome to Questri
          </Typography>
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 16,
              lineHeight: 1.6,
            }}
          >
            {introduction}
          </Typography>
        </Box>

        {isFetching && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <CircularProgress aria-label="Loading your Barns" />
          </Box>
        )}

        {barns.length > 0 && (
          <Box component="section" aria-labelledby="your-barns-heading" sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography id="your-barns-heading" component="h2" variant="h6">Your Barns</Typography>
              <Button onClick={openCreateOrganization} startIcon={<AddRounded />} sx={{ minHeight: 44 }}>
                Create a Barn
              </Button>
            </Box>
            <List
              component="nav"
              aria-label="Your Questri Barns"
              sx={{
                p: 0,
                gap: 1,
              }}
            >
              {barns.map((barn) => {
                const isDeactivated = !barn.application && (barn.deactivated || barn.barnMetadata?.deactivated);
                const barnStatus = isDeactivated
                  ? <Chip color="error" label="Deactivated" size="small" icon={<WarningRounded />} />
                  : (
                    <LaunchRounded
                      sx={{
                        color: 'text.secondary',
                        fontSize: 20,
                      }}
                    />
                  );

                return (
                  <Paper
                    variant="outlined"
                    key={`tenant-list-item-${barn.id}`}
                    sx={{
                      backgroundColor: surfaceBackground,
                      borderRadius: 2,
                      boxShadow: 'none',
                    }}
                  >
                    <ListItemButton
                      component="a"
                      href={`http://${barn.tenant}.${window.location.host}?securityLogin=true`}
                      sx={{
                        p: 2,
                        gap: 1,
                        flexWrap: 'wrap',
                        borderRadius: 'inherit',
                        '&:hover': { backgroundColor: alpha(questriOrange.main, 0.06) },
                        '&.Mui-focusVisible': {
                          outline: `2px solid ${questriOrange.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 48 }}>
                        <TenantAvatar
                          tenant={{
                            displayName: barn.name,
                            iconColor: primary.main,
                            avatar: `https://d8adkpqcrx2wo.cloudfront.net/${barn.tenant}.png`,
                          }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={barn.name}
                        secondary={`${barn.tenant}.questri.app`}
                        sx={{
                          ...barnTextSx,
                          flexBasis: '50%',
                        }}
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexShrink: 0,
                          ml: 'auto',
                        }}
                      >{barnStatus}
                      </Box>
                    </ListItemButton>
                  </Paper>
                );
              })}
            </List>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Paper component="section" aria-labelledby="create-barn-heading" variant="outlined" sx={panelSx}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 1.25,
                mb: 2,
                color: accentColor,
                backgroundColor: alpha(questriOrange.main, 0.1),
                borderRadius: 2,
              }}
            >
              <AddRounded />
            </Box>
            <Typography id="create-barn-heading" component="h2" variant="h6" sx={{ mb: 1 }}>
              Create your Questri Barn
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 15,
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              Bring your horses, people, and daily plan together in one place.
            </Typography>
            <Button
              variant="contained"
              onClick={openCreateOrganization}
              endIcon={<ArrowForwardRounded />}
              sx={{
                ...getQuestriPrimaryButtonSx(),
                minHeight: 48,
                width: {
                  xs: '100%',
                  sm: 'auto',
                },
                px: 2.5,
              }}
            >
              Start your free trial
            </Button>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
              }}
            >
              <CheckRounded
                sx={{
                  color: accentColor,
                  fontSize: 18,
                }}
              />
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: 13,
                  mb: 0,
                }}
              >
                14 days free. No credit card required.
              </Typography>
            </Box>
          </Paper>

          <Paper component="section" aria-labelledby="join-barn-heading" variant="outlined" sx={panelSx}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 1.25,
                mb: 2,
                color: 'text.secondary',
                bgcolor: pageBackground,
                borderRadius: 2,
              }}
            >
              <SearchRounded />
            </Box>
            <Typography id="join-barn-heading" component="h2" variant="h6" sx={{ mb: 1 }}>
              Joining an existing Barn?
            </Typography>
            <Typography
              id="search-organization-text"
              color="text.secondary"
              sx={{
                fontSize: 15,
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              Search for your Barn and request access from its administrator.
            </Typography>
            {requestedBarnName && (
              <Alert severity="success" onClose={() => setRequestedBarnName('')} sx={{ mb: 3 }}>
                Your request to join {requestedBarnName} has been sent to its administrator for approval.
              </Alert>
            )}
            <TextField
              fullWidth
              id="search"
              label="Search by Barn name"
              value={search}
              disabled={isFetching || isSearchingTenants}
              onChange={(e) => { setSearch(e.target.value); }}
              helperText="Enter at least 3 characters."
              slotProps={{
                htmlInput: { 'aria-describedby': 'search-organization-text search-helper-text' },
                input: {
                  startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment>,
                  endAdornment: isSearchingTenants && <CircularProgress size={16} aria-label="Searching Barns" />,
                },
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: 16 } }}
            />
            {foundTenants.length > 0 && (
              <List
                aria-label="Barn search results"
                sx={{
                  p: 0,
                  mt: 2,
                }}
              >
                {foundTenants.map((barn, index) => (
                  <ListItem
                    divider={index < foundTenants.length - 1}
                    key={`tenant-list-item-${barn.id}`}
                    sx={{
                      px: 0,
                      py: 2,
                      gap: 1,
                      flexWrap: 'wrap',
                      '& .MuiButton-root': { opacity: 1 },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <TenantAvatar
                        tenant={{
                          displayName: barn.displayName,
                          iconColor: primary.main,
                          icon: `https://${barn.avatar}`,
                        }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={barn.displayName}
                      secondary={`${barn.name}.questri.app`}
                      sx={{
                        ...barnTextSx,
                        flexBasis: '60%',
                      }}
                    />
                    <Button
                      variant="outlined"
                      aria-label={`Request access to ${barn.displayName}`}
                      disabled={isFetching || isSearchingTenants}
                      sx={{
                        minHeight: 44,
                        ml: 'auto',
                      }}
                      onClick={() => handleRequestAccess(barn)}
                    >
                      Request access
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
            {(hasSearched && isCurrentSearch && !isFetching && !isSearchingTenants && foundTenants.length === 0) && (
              <Typography
                role="status"
                color="text.secondary"
                sx={{
                  fontSize: 14,
                  mt: 2,
                }}
              >
                No Barns found. Try another name, or ask your administrator to invite you.
              </Typography>
            )}
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 13,
                lineHeight: 1.6,
                mt: 2,
                mb: 0,
              }}
            >
              Need a hand? Email <Link href="mailto:support@questri.ai">support@questri.ai</Link>.
            </Typography>
          </Paper>
        </Box>

        <Box
          component="footer"
          sx={{
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography
              component="h2"
              sx={{
                fontSize: 15,
                fontWeight: 600,
                mb: 0.5,
              }}
            >Take your Barn with you
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 13,
                mb: 0,
              }}
            >Get Questri for iOS and Android.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              '& > div': {
                backgroundColor: '#FFFFFF',
                borderRadius: 1,
              },
            }}
          >
            <AppStoreButton store="ios" width={130} height={46} />
            <AppStoreButton store="android" width={130} height={46} />
          </Box>
          {hasProfile && <Button href="/settings/profile" target="_top" sx={{ minHeight: 44 }}>Go to your profile</Button>}
        </Box>
      </Box>
    </Box>
  );
}

LimitedPaidClientAccessCardComponent.propTypes = {
  authUser: PropTypes.object,
  barns: PropTypes.array,
};

LimitedPaidClientAccessCardComponent.defaultProps = {
  authUser: {},
  barns: [],
};

export default LimitedPaidClientAccessCardComponent;
